import React from 'react';
import { useNavigate } from 'react-router-dom';
import './PrivacyPolicy.css';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="privacy-policy-page">
      <div className="privacy-container">
        <button className="back-button" onClick={handleBack}>
          <span className="back-arrow">←</span>
          Back
        </button>
        
        <div className="privacy-content">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: October 22, 2025</p>
          
          <div className="privacy-text">
            <p>
              This Privacy Policy explains how CaseKaizen ("CaseKaizen," "we," "us," or "our") 
              collects, uses, and safeguards your personal information when you access our platform, 
              applications, and related services (collectively, the "Service").
            </p>

            <h2>1. Purpose</h2>
            <p>
              CaseKaizen provides immigration and legal workflow software to help law firms, 
              consultants, and individuals manage cases efficiently across the United States and Canada. 
              This Privacy Policy outlines how we handle personal information in compliance with Canada's 
              Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable U.S. 
              state privacy laws, including the California Consumer Privacy Act (CCPA).
            </p>

            <h2>2. Information We Collect</h2>
            <p>We collect information to deliver and improve our services.</p>

            <h3>a. Personal Information</h3>
            <p>When you use our platform, we may collect:</p>
            <ul>
              <li>Name, email address, phone number, and organization details</li>
              <li>Immigration case information and related documentation</li>
              <li>Payment and billing information (processed via secure third-party providers)</li>
            </ul>

            <h3>b. Device and Usage Information</h3>
            <p>We automatically collect:</p>
            <ul>
              <li>IP address, browser type, device identifiers, and operating system</li>
              <li>Log data and analytics about how you interact with the Service</li>
            </ul>
            <p>
              We use cookies and similar tracking technologies for analytics, personalization, 
              and security purposes.
            </p>

            <h3>c. Sensitive Information</h3>
            <p>
              Documents uploaded by users (e.g., immigration forms, identity proofs, or case evidence) 
              may contain sensitive personal data. We apply strict controls, encryption, and limited 
              access to protect such information.
            </p>

            <h2>3. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul>
              <li>Provide and improve our software and related services</li>
              <li>Enable case collaboration and workflow automation</li>
              <li>Offer technical support and respond to inquiries</li>
              <li>Ensure compliance with legal and regulatory requirements</li>
              <li>Analyze and enhance platform performance and reliability</li>
            </ul>
            <p>We do not sell or rent personal information to third parties.</p>

            <h2>4. Data Storage and Residency</h2>
            <ul>
              <li><strong>Canadian Clients:</strong> Data may be stored in Canadian regions or cloud infrastructure compliant with PIPEDA requirements.</li>
              <li><strong>U.S. Clients:</strong> Data may be hosted in secure U.S.-based facilities.</li>
            </ul>
            <p>All data is encrypted in transit and at rest.</p>
            <p>
              We use reputable cloud providers who meet or exceed industry security and privacy standards.
            </p>

            <h2>5. Sharing and Disclosure</h2>
            <p>We may share limited personal information with:</p>
            <ul>
              <li>Trusted vendors providing hosting, analytics, or payment services</li>
              <li>Legal or regulatory authorities when required by law</li>
              <li>Third parties only with your explicit consent</li>
            </ul>
            <p>
              All vendors are contractually obligated to maintain confidentiality and data protection standards.
            </p>

            <h2>6. Data Retention</h2>
            <p>
              We retain data as long as needed for service delivery, compliance, or as required under 
              client agreements. You may request deletion of your data at any time by contacting{' '}
              <a href="mailto:info@casekaizen.com">info@casekaizen.com</a>.
            </p>

            <h2>7. Your Rights</h2>
            <p>Under PIPEDA and relevant U.S. state laws, you have the right to:</p>
            <ul>
              <li>Access and correct your personal information</li>
              <li>Withdraw consent for data collection or processing</li>
              <li>Request deletion of your personal information</li>
            </ul>
            <p>
              Requests can be sent to <a href="mailto:info@casekaizen.com">info@casekaizen.com</a>.
            </p>

            <h2>8. Security</h2>
            <p>
              We employ strong administrative, physical, and technical measures to protect your information, 
              including encryption, access control, and continuous monitoring. While no system is completely 
              risk-free, we are committed to ongoing security improvements.
            </p>

            <h2>9. Children's Privacy</h2>
            <p>
              Our Services are intended for professional use and not directed toward individuals under 16. 
              We do not knowingly collect personal data from minors.
            </p>

            <h2>10. Updates to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Updates will be posted on our website, 
              and the "Effective Date" will be revised accordingly. Continued use of the Service indicates 
              acceptance of any changes.
            </p>

            <h2>11. Contact Us</h2>
            <p>For questions or privacy-related requests:</p>
            <div className="contact-info">
              <p><strong>CaseKaizen</strong></p>
              <p>📧 <a href="mailto:info@casekaizen.com">info@casekaizen.com</a></p>
              <p>📍 200 Innovative Way, Nashua, NH 03062</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
